import React, { Fragment, useState, useEffect, useMemo, useCallback } from "react";
import {
  Card,
  Col,
  Row,
  Breadcrumb,
  Button,
  Nav,
  Tab,
  Modal,
  Form,
} from "react-bootstrap";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faCalendar,
  faChevronDown,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import "./automatizacion.scss";
import ReactApexChart from 'react-apexcharts';
import { estadisticasAutomatizacionService } from '../../services/estadisticasAutomatizacionService';
import { useAuth } from '../../hooks/useAuth';

const Automatizacion = () => {
  // Hook de autenticación
  const { user, asesorId, isAuthenticated } = useAuth();

  const [activeTab, setActiveTab] = useState("recordatorios");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    dateRange: {
      start: "15/11/2024",
      end: "04/12/2024",
    },
    temperature: ["Frío", "Tibio", "Caliente"],
  });

  // Estados para el filtro de fechas
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');

  // Estado para todas las estadísticas de automatización
  const [estadisticasAutomatizacion, setEstadisticasAutomatizacion] = useState({
    recordatoriosCitas: {
      enviados: { total: 0, porcentajeTotal: 0 },
      vistos: { total: 0, porcentajeTotal: 0 },
      respondidos: { total: 0, porcentajeTotal: 0 }
    },
    citasReprogramadas: {
      totalCitas: 0,
      totalReprogramadas: 0
    },
    mensajesRecuperacion: {
      enviados: { total: 0, porcentajeTotal: 0 },
      vistos: { total: 0, porcentajeTotal: 0 },
      respondidos: { total: 0, porcentajeTotal: 0 }
    }
  });

  // Estado separado para los datos del gráfico de evolución (solo se actualiza al aplicar filtro)
  const [datosGraficoEvolucion, setDatosGraficoEvolucion] = useState([]);

  const [loadingEstadisticas, setLoadingEstadisticas] = useState(false);

  // Función para obtener el primer y último día del mes actual
  const obtenerRangoMesActual = () => {
    const fechaActual = new Date();
    const primerDiaMes = new Date(fechaActual.getFullYear(), fechaActual.getMonth(), 1);
    const ultimoDiaMes = new Date(fechaActual.getFullYear(), fechaActual.getMonth() + 1, 0);
    
    // Formatear fechas para input type="date" (YYYY-MM-DD)
    const formatearFecha = (fecha) => {
      const año = fecha.getFullYear();
      const mes = String(fecha.getMonth() + 1).padStart(2, '0');
      const dia = String(fecha.getDate()).padStart(2, '0');
      return `${año}-${mes}-${dia}`;
    };

    return {
      desde: formatearFecha(primerDiaMes),
      hasta: formatearFecha(ultimoDiaMes)
    };
  };

  // Establecer fechas por defecto al montar el componente
  useEffect(() => {
    const rangoMes = obtenerRangoMesActual();
    setFechaDesde(rangoMes.desde);
    setFechaHasta(rangoMes.hasta);
  }, []);

  // Función para cargar estadísticas
  const cargarEstadisticas = async (fechaDesdeParam = null, fechaHastaParam = null) => {
    try {
      setLoadingEstadisticas(true);

      // Obtener el token del localStorage
      const token = localStorage.getItem('token');

      if (!token) {
        console.error('No hay token de autenticación');
        return;
      }

      // Usar las fechas del parámetro o del estado
      const fechaDesdeFinal = fechaDesdeParam || fechaDesde;
      const fechaHastaFinal = fechaHastaParam || fechaHasta;

      const data = await estadisticasAutomatizacionService.getEstadisticasAutomatizacion(token, fechaDesdeFinal, fechaHastaFinal);
      setEstadisticasAutomatizacion(data);
      
      // Actualizar los datos del gráfico de evolución solo cuando se cargan nuevos datos
      if (data.mensajesRecuperacion && data.mensajesRecuperacion.evolucion) {
        setDatosGraficoEvolucion(data.mensajesRecuperacion.evolucion);
      }
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    } finally {
      setLoadingEstadisticas(false);
    }
  };

  // Cargar datos al montar el componente
  useEffect(() => {
    // Solo cargar si el usuario está autenticado
    if (isAuthenticated) {
      cargarEstadisticas();
    }
  }, [isAuthenticated]);

  const funnelData = {
    envios: 5000,
    visto: 3800,
    respondido: 2200,
    desuscrito: 1500,
  };

  // Colores de Resumen.jsx
  const funnelColors = {
    envios: '#E8EAF6',
    visto: '#C5CAE9',
    respondido: '#9FA8DA',
    desuscrito: '#6A8BFF',
  };

  const evolutionColors = [
    '#6A8BFF', // desuscritos
    '#8095E4', // respondido
    '#B6C5FF', // vistos
    '#E8EAF6', // envios
  ];

  const evolutionData = [
    {
      month: "Ene",
      desuscritos: 150,
      respondido: 280,
      vistos: 420,
      envios: 800,
    },
    {
      month: "Feb",
      desuscritos: 160,
      respondido: 290,
      vistos: 410,
      envios: 780,
    },
    {
      month: "Mar",
      desuscritos: 155,
      respondido: 285,
      vistos: 415,
      envios: 790,
    },
    {
      month: "Abr",
      desuscritos: 165,
      respondido: 295,
      vistos: 425,
      envios: 810,
    },
    {
      month: "May",
      desuscritos: 160,
      respondido: 290,
      vistos: 420,
      envios: 800,
    },
    {
      month: "Jun",
      desuscritos: 155,
      respondido: 285,
      vistos: 415,
      envios: 790,
    },
    {
      month: "Jul",
      desuscritos: 150,
      respondido: 280,
      vistos: 410,
      envios: 780,
    },
    {
      month: "Ago",
      desuscritos: 550,
      respondido: 280,
      vistos: 410,
      envios: 780,
    },
    {
      month: "Set",
      desuscritos: 160,
      respondido: 290,
      vistos: 420,
      envios: 800,
    },
    {
      month: "Oct",
      desuscritos: 155,
      respondido: 285,
      vistos: 415,
      envios: 790,
    },
    {
      month: "Nov",
      desuscritos: 165,
      respondido: 295,
      vistos: 425,
      envios: 810,
    },
    {
      month: "Dic",
      desuscritos: 160,
      respondido: 290,
      vistos: 420,
      envios: 800,
    },
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{label}, 2024</p>
          <p className="tooltip-value">
            {payload[0].value} {payload[0].name}
          </p>
        </div>
      );
    }
    return null;
  };

  const handleRemoveTemperature = (temp) => {
    setFilters((prev) => ({
      ...prev,
      temperature: prev.temperature.filter((t) => t !== temp),
    }));
  };

  const FilterModal = () => (
    <Modal
      show={showFilters}
      onHide={() => setShowFilters(false)}
      dialogClassName="filter-modal"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faFilter} className="me-2" />
          Filtros
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="filters-container">
          <div className="filter-group">
            <div className="filter-label">
              <Form.Label>Fecha</Form.Label>
              <FontAwesomeIcon icon={faChevronDown} />
            </div>
            <div className="date-range">
              <div className="date-input">
                <span className="date-label">Del</span>
                <div className="input-with-icon">
                  <Form.Control
                    type="text"
                    value={filters.dateRange.start}
                    readOnly
                  />
                  <FontAwesomeIcon icon={faCalendar} />
                </div>
              </div>
              <div className="date-input">
                <span className="date-label">Al</span>
                <div className="input-with-icon">
                  <Form.Control
                    type="text"
                    value={filters.dateRange.end}
                    readOnly
                  />
                  <FontAwesomeIcon icon={faCalendar} />
                </div>
              </div>
              <Button variant="outline-danger" className="remove-filter">
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </div>
          </div>

          <div className="filter-group">
            <div className="filter-label">
              <Form.Label>Temperatura</Form.Label>
              <FontAwesomeIcon icon={faChevronDown} />
            </div>
            <div className="temperature-tags">
              <div className="selected-temperatures">
                {filters.temperature.map((temp) => (
                  <div key={temp} className="temperature-tag">
                    {temp}
                    <Button
                      variant="link"
                      className="remove-tag"
                      onClick={() => handleRemoveTemperature(temp)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </div>
                ))}
              </div>
              <Button variant="outline-danger" className="remove-filter">
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </div>
          </div>

          <Button variant="primary" className="add-filter-btn">
            + Añadir filtro
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );

  const EmbudoInteraccionesVisual = () => {
    const steps = [
      { porcentaje: '100%', label: 'Envíos', value: '5,000', color: '#E8EAF6', width: '100%' },
      { porcentaje: '76%', label: 'Visto', value: '3,800', color: '#C5CAE9', width: '85%' },
      { porcentaje: '57.8%', label: 'Respondido', value: '2,200', color: '#9FA8DA', width: '70%' },
      { porcentaje: '68.2%', label: 'Desuscrito', value: '1,500', color: '#6A8BFF', width: '55%' },
    ];
    return (
      <div style={{ width: '100%', padding: '8px 0' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {steps.map((step, i) => (
            <div key={i} style={{
              background: step.color,
              borderRadius: 12,
              padding: '18px 32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontWeight: 500,
              fontSize: 20,
              color: '#222',
              minWidth: 220,
              width: step.width,
              margin: '0 auto',
              boxShadow: '0 1px 4px 0 rgba(80,80,120,0.03)'
            }}>
              <span style={{ fontWeight: 700, fontSize: 24, color: (step.color === '#6A8BFF' || step.color === '#8095E4') ? '#fff' : '#6A8BFF', minWidth: 70 }}>{step.porcentaje}</span>
              <span style={{ opacity: 0.7, marginLeft: 16, flex: 1 }}>{step.label}</span>
              <span style={{ fontWeight: 700, fontSize: 20, marginLeft: 16 }}>{step.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const EmbudoRecordatoriosVisual = () => {

    // Calcular porcentajes basados en los datos reales de recordatorios de citas
    const totalEnviados = estadisticasAutomatizacion.recordatoriosCitas.enviados.total;
    const totalVistos = estadisticasAutomatizacion.recordatoriosCitas.vistos.total;
    const totalRespondidos = estadisticasAutomatizacion.recordatoriosCitas.respondidos.total;

    const porcentajeEnviados = estadisticasAutomatizacion.recordatoriosCitas.enviados.porcentajeTotal;
    const porcentajeVistos = estadisticasAutomatizacion.recordatoriosCitas.vistos.porcentajeTotal;
    const porcentajeRespondidos = estadisticasAutomatizacion.recordatoriosCitas.respondidos.porcentajeTotal;

    const steps = [
      {
        porcentaje: `${porcentajeEnviados}%`,
        label: 'Enviados',
        value: totalEnviados.toLocaleString(),
        color: '#6A8BFF',
        width: '100%'
      },
      {
        porcentaje: `${porcentajeVistos}%`,
        label: 'Vistos',
        value: totalVistos.toLocaleString(),
        color: '#8095E4',
        width: '90%'
      },
      {
        porcentaje: `${porcentajeRespondidos}%`,
        label: 'Respondidos',
        value: totalRespondidos.toLocaleString(),
        color: '#B6C5FF',
        width: '70%'
      },
    ];
    return (
      <div style={{ width: '100%', height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}>
          {steps.map((step, i) => (
            <div key={i} style={{
              background: step.color,
              borderRadius: 12,
              padding: '18px 32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontWeight: 500,
              fontSize: 18,
              color: '#fff',
              minWidth: 250,
              width: step.width,
              margin: '0 auto',
              boxShadow: '0 1px 4px 0 rgba(80,80,120,0.03)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <span style={{ fontWeight: 700, fontSize: 22, minWidth: 60, flexShrink: 0 }}>{step.porcentaje}</span>
              <span style={{ opacity: 0.9, marginLeft: 16, flex: 1, textAlign: 'center', flexShrink: 0 }}>{step.label}</span>
              <span style={{ fontWeight: 700, fontSize: 18, marginLeft: 16, minWidth: 50, textAlign: 'right', flexShrink: 0 }}>{step.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const DonutReagendamiento = () => {
    // Usar datos reales de citas reprogramadas
    const totalCitas = estadisticasAutomatizacion.citasReprogramadas.totalCitas;
    const totalReprogramadas = estadisticasAutomatizacion.citasReprogramadas.totalReprogramadas;
    const totalNoReprogramadas = totalCitas - totalReprogramadas;

    const porcentajeReprogramadas = totalCitas > 0 ? Math.round((totalReprogramadas / totalCitas) * 100) : 0;
    const porcentajeNoReprogramadas = totalCitas > 0 ? Math.round((totalNoReprogramadas / totalCitas) * 100) : 0;

    const series = [porcentajeReprogramadas, porcentajeNoReprogramadas];
    const labels = ['Sí', 'No'];
    const colors = ['#6A8BFF', '#B6C5FF'];
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 260 }}>
        <div style={{ width: 220, height: 220, position: 'relative' }}>
          <ReactApexChart
            options={{
              chart: { type: 'donut', height: 220 },
              labels,
              colors,
              dataLabels: { enabled: false },
              legend: { show: false },
              plotOptions: { pie: { donut: { size: '75%' } } },
              stroke: { width: 0 }
            }}
            series={series}
            type="donut"
            height={220}
          />
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}>
            <span style={{ fontSize: 48, fontWeight: 700, color: '#222', lineHeight: 1 }}>
              {porcentajeReprogramadas}%
            </span>
          </div>
        </div>
        <div style={{ marginLeft: 32 }}>
          {labels.map((label, i) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', marginBottom: i < labels.length - 1 ? 12 : 0 }}>
              <span style={{ width: 14, height: 14, borderRadius: '50%', background: colors[i], display: 'inline-block', marginRight: 8 }}></span>
              <span style={{ color: '#222', fontWeight: 500, marginRight: 8 }}>{label}</span>
              <span style={{ color: '#222', fontWeight: 700, minWidth: 40, textAlign: 'right', display: 'inline-block' }}>{series[i]}%</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Embudo visual para mensajes de recuperación
  const EmbudoRecuperacionVisual = () => {
    // Usar datos reales de mensajes de recuperación
    const totalEnviados = estadisticasAutomatizacion.mensajesRecuperacion.enviados.total;
    const totalVistos = estadisticasAutomatizacion.mensajesRecuperacion.vistos.total;
    const totalRespondidos = estadisticasAutomatizacion.mensajesRecuperacion.respondidos.total;

    const porcentajeVistos = totalEnviados > 0 ? Math.round((totalVistos / totalEnviados) * 100) : 0;
    const porcentajeRespondidos = totalEnviados > 0 ? Math.round((totalRespondidos / totalEnviados) * 100) : 0;

    const steps = [
      {
        porcentaje: '100%',
        label: 'Enviados',
        value: totalEnviados.toLocaleString(),
        color: '#6A8BFF',
        width: '100%'
      },
      {
        porcentaje: `${porcentajeVistos}%`,
        label: 'Vistos',
        value: totalVistos.toLocaleString(),
        color: '#8095E4',
        width: '90%'
      },
      {
        porcentaje: `${porcentajeRespondidos}%`,
        label: 'Respondidos',
        value: totalRespondidos.toLocaleString(),
        color: '#B6C5FF',
        width: '70%'
      },
    ];
    return (
      <div style={{ width: '100%', height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}>
          {steps.map((step, i) => (
            <div key={i} style={{
              background: step.color,
              borderRadius: 12,
              padding: '18px 32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontWeight: 500,
              fontSize: 20,
              color: '#fff',
              minWidth: 220,
              width: step.width,
              margin: '0 auto',
              boxShadow: '0 1px 4px 0 rgba(80,80,120,0.03)'
            }}>
              <span style={{ fontWeight: 700, fontSize: 24, minWidth: 70 }}>{step.porcentaje}</span>
              <span style={{ opacity: 0.7, marginLeft: 16, flex: 1 }}>{step.label}</span>
              <span style={{ fontWeight: 700, fontSize: 20, marginLeft: 16 }}>{step.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Memoizar los datos procesados del gráfico de evolución
  const datosProcesadosEvolucion = useMemo(() => {
    return datosGraficoEvolucion.map(item => ({
      ...item,
      mesAno: `${item.month}-${item.year}` // Crear etiqueta combinada
    }));
  }, [datosGraficoEvolucion]);

  // Gráfico de evolución para mensajes de recuperación (completamente independiente)
  const EvolucionRecuperacionChart = useCallback(() => {
    const colors = ['#6A8BFF', '#8095E4', '#B6C5FF'];
    
    return (
      <div style={{ width: '100%', height: 260 }}>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={datosProcesadosEvolucion} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis 
              dataKey="mesAno" 
              axisLine={false} 
              tickLine={false} 
              tick={{ fill: '#7987a1', fontSize: 12 }} 
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis axisLine={false} tickLine={false} tick={{ fill: '#7987a1' }} />
            <Tooltip contentStyle={{ fontSize: 14 }} />
            <Area type="monotone" dataKey="enviados" stroke={colors[2]} fill={colors[2]} name="Enviados" />
            <Area type="monotone" dataKey="vistos" stroke={colors[1]} fill={colors[1]} name="Vistos" />
            <Area type="monotone" dataKey="respondidos" stroke={colors[0]} fill={colors[0]} name="Respondidos" />            
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  }, [datosProcesadosEvolucion]);

  return (
    <div className="automatizacion-container">
      <div className="breadcrumb-header">
        <Breadcrumb>
          <Breadcrumb.Item>Dashboard</Breadcrumb.Item>
          <Breadcrumb.Item active>Automatización</Breadcrumb.Item>
        </Breadcrumb>
      </div>

      <div className="automatizacion-main-container">
        <div className="page-header">
          <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h5 className="page-title">Automatización</h5>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ fontSize: 14, fontWeight: 500, color: '#666' }}>Desde:</label>
                <input
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ fontSize: 14, fontWeight: 500, color: '#666' }}>Hasta:</label>
                <input
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <button
                onClick={() => {
                  console.log('Aplicando filtro desde:', fechaDesde, 'hasta:', fechaHasta);
                  // Recargar las gráficas con el nuevo rango de fechas
                  cargarEstadisticas(fechaDesde, fechaHasta);
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6A8BFF',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Aplicar Filtro
              </button>
            </div>
          </div>
          <div className="automatizacion-separador"></div>
        </div>

        <Tab.Container defaultActiveKey="recordatorios">
          <Nav
            variant="pills"
            className="nav nav-tabs automatizacion-tab-style nav-justified mb-3 d-sm-flex d-block"
            id="automatizacionTab"
            role="tablist"
          >
            {/* <Nav.Item>
              <Nav.Link
                eventKey="campanas"
                id="campanas-tab"
                type="button"
                className="border-0 bg-transparent"
              >
                Campañas
              </Nav.Link>
            </Nav.Item> */}
            <Nav.Item>
              <Nav.Link
                eventKey="recordatorios"
                id="recordatorios-tab"
                type="button"
                className="border-0 bg-transparent"
              >
                Recordatorios de citas
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="mensajes"
                id="mensajes-tab"
                type="button"
                className="border-0 bg-transparent"
              >
                Mensajes de recuperación
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            {/* <Tab.Pane eventKey="campanas">
              <h2 className="section-title">Campañas</h2>
              <p className="section-description">
                Esta sección muestra el conteo y la evolución de los mensajes
                enviados, vistos, respondidos y desuscritos.
              </p>

              <Row className="row-sm">
                <Col lg={6}>
                  <Card className="custom-card">
                    <Card.Body>
                      <h3 className="card-title">Embudo de interacciones</h3>
                      <div className="card-title-separator" style={{ borderBottom: '1px solid #e9ecef', marginBottom: 16, marginTop: 8 }} />
                      <EmbudoInteraccionesVisual />
                    </Card.Body>
                  </Card>
                </Col>
                <Col lg={6}>
                  <Card className="custom-card">
                    <Card.Body>
                      <h3 className="card-title">Evolución de las interacciones</h3>
                      <div className="card-title-separator" style={{ borderBottom: '1px solid #e9ecef', marginBottom: 16, marginTop: 8 }} />
                      <div className="evolution-chart">
                        <ResponsiveContainer width="100%" height={340}>
                          <AreaChart
                            data={evolutionData}
                            margin={{
                              top: 10,
                              right: 30,
                              left: 0,
                              bottom: 0,
                            }}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              vertical={false}
                            />
                            <XAxis
                              dataKey="month"
                              axisLine={false}
                              tickLine={false}
                              tick={{ fill: "#7987a1" }}
                            />
                            <YAxis
                              axisLine={false}
                              tickLine={false}
                              tick={{ fill: "#7987a1" }}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Area
                              type="monotone"
                              dataKey="desuscritos"
                              stackId="1"
                              stroke={evolutionColors[0]}
                              fill={evolutionColors[0]}
                              name="Desuscritos"
                            />
                            <Area
                              type="monotone"
                              dataKey="respondido"
                              stackId="1"
                              stroke={evolutionColors[1]}
                              fill={evolutionColors[1]}
                              name="Respondido"
                            />
                            <Area
                              type="monotone"
                              dataKey="vistos"
                              stackId="1"
                              stroke={evolutionColors[2]}
                              fill={evolutionColors[2]}
                              name="Vistos"
                            />
                            <Area
                              type="monotone"
                              dataKey="envios"
                              stackId="1"
                              stroke={evolutionColors[3]}
                              fill={evolutionColors[3]}
                              name="Envíos"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab.Pane> */}
            <Tab.Pane eventKey="recordatorios">
              <div className="section-header">
                <h2 className="section-title">Recordatorios de citas</h2>
                <p className="section-description">
                  En este apartado podrás visualizar los recordatorios de citas a través de las siguientes métricas: enviados, vistos y respondidos.
                </p>
              </div>
              <Row>
                <Col md={6}>
                  <Card className="custom-card">
                    <Card.Header>
                      <Card.Title>Progreso de los recordatorios de citas</Card.Title>
                    </Card.Header>
                    <Card.Body>
                      {loadingEstadisticas ? (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: 200,
                          flexDirection: 'column',
                          gap: 16
                        }}>
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Cargando...</span>
                          </div>
                          <span>Cargando estadísticas...</span>
                        </div>
                      ) : (
                        <EmbudoRecordatoriosVisual />
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="custom-card">
                    <Card.Header>
                      <Card.Title>Reagendamiento</Card.Title>
                    </Card.Header>
                    <Card.Body>
                      <DonutReagendamiento />
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab.Pane>
            <Tab.Pane eventKey="mensajes">
              <div className="section-header">
                <h2 className="section-title">Mensajes de recuperación</h2>
                <p className="section-description">
                  En este apartado podrás visualizar los mensajes de recuperación diseñados para reducir el abandono, a través de las siguientes métricas: enviados, vistos y respondidos.
                </p>
              </div>
              <Row>
                <Col md={6}>
                  <Card className="custom-card">
                    <Card.Header>
                      <Card.Title>Progreso de los mensajes de recuperación</Card.Title>
                    </Card.Header>
                    <Card.Body style={{ minHeight: 340, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ width: '100%' }}>
                        <EmbudoRecuperacionVisual />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="custom-card">
                    <Card.Header>
                      <Card.Title>Evolución de los mensajes de recuperación</Card.Title>
                    </Card.Header>
                    <Card.Body style={{ minHeight: 340, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ width: '100%' }}>
                        <EvolucionRecuperacionChart />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>

        <FilterModal />
      </div>
    </div>
  );
};

export default Automatizacion;
